import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Filter, Loader2, MapPin, Search, Star } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Camper "Explore" screen — searchable campsite feed backed by GET /api/campsites.
// Mirrors the mobile app's Explore filter chip strip (All + state chips) and
// full-text search over the campsite name.
const STATES_MY = [
  "All", "Johor", "Kedah", "Kelantan", "Melaka", "Negeri Sembilan",
  "Pahang", "Perak", "Perlis", "Pulau Pinang", "Sabah", "Sarawak",
  "Selangor", "Terengganu", "WP Kuala Lumpur", "WP Labuan", "WP Putrajaya",
];

export default function Explore() {
  const { api } = useAuth();
  const [search, setSearch] = useState("");
  const [state, setState] = useState("All");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/campsites", {
          params: {
            paginated: true,
            limit: 60,
            offset: 0,
            search: search.trim() || undefined,
            state: state !== "All" ? state : undefined,
          },
        });
        if (!cancelled) setItems(data?.items || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load campsites.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, search ? 300 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [api, search, state]);

  const empty = items && items.length === 0;

  return (
    <div className="flex flex-col gap-5" data-testid="explore-page">
      <header className="flex flex-col gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold" style={{ color: "var(--on-surface)" }}>
            Explore campsites
          </h1>
          <p className="text-sm" style={{ color: "var(--on-surface-muted)" }}>
            Discover verified spots across Malaysia. Save your favourites for next weekend.
          </p>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="w-full pl-11 pr-4 py-3 rounded-2xl border text-base"
            style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
            data-testid="explore-search"
          />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 no-scrollbar">
          <Filter className="w-4 h-4 opacity-60 shrink-0" />
          {STATES_MY.map((s) => (
            <button
              key={s}
              onClick={() => setState(s)}
              className="whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-widest transition shrink-0"
              style={{
                background: state === s ? "var(--brand)" : "color-mix(in srgb, var(--brand) 10%, transparent)",
                color: state === s ? "white" : "var(--brand)",
              }}
              data-testid={`chip-state-${s.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {s}
            </button>
          ))}
        </div>
      </header>

      {error && <div className="card-elev p-4" style={{ color: "var(--premium)" }}>{error}</div>}

      {loading && !items && (
        <div className="card-elev p-6 flex items-center gap-3" style={{ color: "var(--on-surface-muted)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading campsites…
        </div>
      )}

      {empty && !loading && (
        <div className="card-elev p-8 text-center" data-testid="explore-empty">
          <div className="text-lg font-extrabold mb-1" style={{ color: "var(--on-surface)" }}>No campsites match yet</div>
          <div className="text-sm" style={{ color: "var(--on-surface-muted)" }}>Try a different state or clear your search.</div>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="explore-grid">
          {items.map((c) => <CampsiteCard key={c.campsite_id} c={c} />)}
        </div>
      )}
    </div>
  );
}

function CampsiteCard({ c }) {
  const rating = c.rating_avg ?? c.avg_rating ?? c.rating ?? null;
  const verified = c.verification_status === "verified";
  // Mobile backend returns the campsite image as either `photo_thumb`
  // (small base-64 data URL) or `photo` on some endpoints. Try both so
  // the Explore card always renders an image when one exists.
  const photo = c.photo_thumb || c.photo || c.photo_full || c.logo_data || null;
  return (
    <Link
      to={`/campsites/${c.campsite_id}`}
      className="card-elev overflow-hidden hover:-translate-y-0.5 transition block"
      data-testid={`campsite-card-${c.campsite_id}`}
    >
      <div className="aspect-[16/10] bg-black/10 overflow-hidden">
        {photo ? (
          <img src={photo} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ color: "var(--on-surface-muted)" }}>
            <MapPin className="w-8 h-8 opacity-40" />
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-extrabold text-base leading-tight" style={{ color: "var(--on-surface)" }}>
            {c.name}
          </h3>
          {verified && (
            <span className="shrink-0 text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                  style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
              Verified
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs" style={{ color: "var(--on-surface-muted)" }}>
          <MapPin className="w-3.5 h-3.5" /> {c.state || c.location || "Malaysia"}
        </div>
        <div className="mt-1 flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1 font-bold" style={{ color: "var(--brand)" }}>
            Lvl {c.level ?? 1} · {c.title || "Rookie Site"}
          </span>
          {rating != null && (
            <span className="inline-flex items-center gap-1 font-bold" style={{ color: "var(--premium)" }}>
              <Star className="w-3 h-3" fill="currentColor" /> {Number(rating).toFixed(1)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
